import { useCallback, useMemo, useState } from 'react';

import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElementOptions } from '@stripe/stripe-js';
import isEmpty from 'lodash-es/isEmpty';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCloseModal } from '@/hooks/useCloseModal';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { safelyGetErrorMessage } from '@/util/errors';
import { isRequired } from '@/util/validation/constants';

import { WithStripeElements } from './WithStripeElements';

// Stripe's iframe is a separate document, so hand it concrete colors, not `var(--color-*)`.
const resolveTokenColor = (token: string): string => {
  const probe = document.createElement('span');
  probe.style.color = `var(${token})`;
  probe.style.display = 'none';
  document.body.append(probe);
  const color = getComputedStyle(probe).color;
  probe.remove();
  return color;
};

const buildCardStyle = (): StripeCardElementOptions => ({
  style: {
    base: {
      color: resolveTokenColor('--color-foreground'),
      fontFamily: getComputedStyle(document.body).fontFamily,
      fontSize: '14px',
      '::placeholder': { color: resolveTokenColor('--color-foreground-muted') },
    },
    invalid: { color: '#f00', iconColor: '#f00' },
  },
});

type Form = { cardholderName: string };

interface Props {
  onSuccess?: () => void;
}

const ModalAddCardContent = ({ onSuccess }: Props) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>();
  const { attachPaymentMethod, paymentMethods, updateCustomerAsync } = useBilling();
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);

  const cardStyle = useMemo(buildCardStyle, []);

  const closeModal = useCloseModal();

  const onSubmit = useCallback(async () => {
    if (!elements || !stripe) {
      return;
    }
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }
    setIsLoading(true);
    try {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      if (!paymentMethod) {
        throw error ?? new Error(t('billing.payment.cannot_be_created'));
      }
      // Attach to customer
      let makeDefault = false;
      if (isEmpty(paymentMethods)) {
        makeDefault = true;
      }
      attachPaymentMethod(paymentMethod, {
        onError: (err) => {
          throw err;
        },
        onSuccess: async () => {
          if (makeDefault) {
            await updateCustomerAsync({
              invoice_settings: { default_payment_method: paymentMethod.id },
            }).catch((err) => {
              throw err;
            });
          }
          toast.success(t('billing.payment_method_added'));
          closeModal();
          if (onSuccess) {
            onSuccess();
          }
        },
      });
    } catch (error) {
      toast.error(safelyGetErrorMessage(error));
      setIsLoading(false);
    }
  }, [
    elements,
    stripe,
    paymentMethods,
    attachPaymentMethod,
    t,
    onSuccess,
    updateCustomerAsync,
    closeModal,
  ]);

  return (
    <FormDialog
      title={t('billing.payment_add_card')}
      description={t('billing.payment_enter_card_details')}
      submitLabel={t('common.add')}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isLoading}
      submitOnEnter
    >
      <form className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="cardholderName">{t('billing.payment.cardholder_name')}</Label>
          <Controller
            control={control}
            name="cardholderName"
            rules={isRequired}
            render={({ field }) => (
              <Input id="cardholderName" {...field} value={field.value ?? ''} />
            )}
          />
          {errors.cardholderName?.message && (
            <p className="text-sm text-destructive">{errors.cardholderName.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor="card-element">{t('billing.payment_credit_or_debit')}</Label>
          <div className="h-10 rounded-md border border-stroke bg-background px-4 py-3 text-sm leading-5">
            <CardElement id="card-element" options={cardStyle} />
          </div>
        </div>
      </form>
    </FormDialog>
  );
};

export const ModalAddCard = WithStripeElements(ModalAddCardContent);
